import React from "react";
import { useLocation, Link } from "react-router-dom";
import clsx from "clsx";

import {
  bulkEditClassesPath,
  bulkEditNextYearClassesPath,
  bulkEditNextYearStudentsPath,
  bulkEditStudentsPath,
  bulkRegisterTeachersPath,
  classesPath,
  copyNotificationPath,
  editClassPath,
  editNextYearClassPath,
  editNextYearStudentPath,
  editNotificationPath,
  editStudentPath,
  editSubClassPath,
  editSubClassStudentPath,
  editTeacherPath,
  exchangeSchoolYearPath,
  gearStatusPath,
  homePath,
  newClassPath,
  newNextYearClassPath,
  newNextYearStudentPath,
  newNotificationPath,
  newStudentPath,
  newSubClassPath,
  newTeacherPath,
  nextYearClassesPath,
  nextYearStudentsPath,
  notificationsPath,
  studentsPath,
  subClassesPath,
  teachersPath,
  uploadStudentRealPhotosPath,
  useHistoriesPath,
  subjectsPath,
} from "../../../managerStore";
import {
  NAV_ITEMS,
  REGISTRATION_STEPS_REQUIRED,
  REGISTRATION_STEPS_REQUIRED_DLP_ONLY,
  REGISTRATION_STEPS_OPTIONAL,
  MANAGE,
} from "../../../constants/navigation";
import { NavItemKey } from "../../../state";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import useAppOrderStatus from "../../../hooks/manager/useAppOrderStatus";
import useSchoolStatus from "../../../hooks/manager/useSchoolStatus";
import useNavLabel from "../../../hooks/manager/useNavLabel";
import Modal from "../../../atoms/Modal";

import styles from "./SideMenuBar.module.scss";

const pathsList = [
  [teachersPath, newTeacherPath, editTeacherPath, bulkRegisterTeachersPath],
  [classesPath, newClassPath, editClassPath, bulkEditClassesPath],
  [studentsPath, newStudentPath, editStudentPath, bulkEditStudentsPath],
  [
    exchangeSchoolYearPath,
    nextYearClassesPath,
    newNextYearClassPath,
    editNextYearClassPath,
    bulkEditNextYearClassesPath,
    nextYearStudentsPath,
    newNextYearStudentPath,
    editNextYearStudentPath,
    bulkEditNextYearStudentsPath,
  ],
  [gearStatusPath],
  [subClassesPath, newSubClassPath, editSubClassPath, editSubClassStudentPath],
  [
    notificationsPath,
    newNotificationPath,
    editNotificationPath,
    copyNotificationPath,
  ],
  [useHistoriesPath],
  [subjectsPath],
  [uploadStudentRealPhotosPath],
];

const matchPath = (currentPath: string, paths: string[]) =>
  paths.some(path => currentPath.includes(path));

export const SideMenuBar = (props: { logout: () => void }) => {
  const location = useLocation();
  const [showModal, setShowModal] = React.useState(false);
  const { nextYear } = useCurrentYear();
  const { hasOnlyDlpOrder } = useAppOrderStatus();
  const { isCramSchool, isCurrentYearChangeOpen } = useSchoolStatus();
  const { getNavLabel } = useNavLabel();

  const activePath = React.useMemo(() => {
    if (location.pathname === homePath) {
      return homePath;
    }

    // select first path of matched paths
    const matchedPath = pathsList.find(paths =>
      matchPath(location.pathname, paths),
    )?.[0];

    return matchedPath ? matchedPath : "";
  }, [location.pathname]);

  const registrationStepsRequired = hasOnlyDlpOrder
    ? REGISTRATION_STEPS_REQUIRED_DLP_ONLY
    : REGISTRATION_STEPS_REQUIRED;

  const isNavItemActive = (key: NavItemKey) =>
    activePath === NAV_ITEMS[key].link && styles.isActive;

  return (
    <>
      <div className={styles.sideBar}>
        <div>
          <ul className={styles.list}>
            <li className={styles.item}>
              <Link
                className={clsx(styles.link, isNavItemActive("home"))}
                to={NAV_ITEMS["home"].link}
              >
                {getNavLabel("home")}
              </Link>
            </li>
            {isCurrentYearChangeOpen && (
              <li className={styles.item}>
                <Link
                  className={clsx(
                    styles.link,
                    isNavItemActive("exchangeSchoolYear"),
                  )}
                  to={NAV_ITEMS["exchangeSchoolYear"].link}
                >
                  <p>{getNavLabel("exchangeSchoolYear")}</p>
                  <p className={styles.caution}>
                    ※{nextYear}年度の利用前に
                    <br />
                    年度切替が必要です
                  </p>
                </Link>
              </li>
            )}
          </ul>
          <ul className={styles.list}>
            <li
              className={clsx(
                styles.heading,
                styles[registrationStepsRequired.kind],
              )}
            >
              {registrationStepsRequired.label}
            </li>
            {registrationStepsRequired.navItemKeys.map((key, index) => {
              const no = index + 1;
              return (
                <li key={key} className={styles.item}>
                  <Link
                    className={clsx(styles.link, isNavItemActive(key))}
                    to={NAV_ITEMS[key].link}
                  >
                    {no}.{getNavLabel(key)}
                  </Link>
                </li>
              );
            })}
          </ul>
          {!hasOnlyDlpOrder && (
            <>
              <ul className={styles.list}>
                <li
                  className={clsx(
                    styles.heading,
                    styles[REGISTRATION_STEPS_OPTIONAL.kind],
                  )}
                >
                  {REGISTRATION_STEPS_OPTIONAL.label}
                </li>
                {REGISTRATION_STEPS_OPTIONAL.navItemKeys.map((key, index) => {
                  const no =
                    registrationStepsRequired.navItemKeys.length + index + 1;

                  return (
                    <li key={key} className={styles.item}>
                      <Link
                        className={clsx(styles.link, isNavItemActive(key))}
                        to={NAV_ITEMS[key].link}
                      >
                        {no}.{getNavLabel(key)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <ul className={styles.list}>
                <li className={clsx(styles.heading, styles[MANAGE.kind])}>
                  {MANAGE.label}
                </li>
                {MANAGE.navItemKeys.map(key => {
                  if (!isCramSchool && key === "useHistories") return null;

                  return (
                    <li key={key} className={styles.item}>
                      <Link
                        className={clsx(styles.link, isNavItemActive(key))}
                        to={NAV_ITEMS[key].link}
                      >
                        {getNavLabel(key)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <button className={styles.logout} onClick={() => setShowModal(true)}>
          ログアウト
        </button>
      </div>
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className={styles.description}>
            ログアウトしてよろしいですか？
          </div>
          <div className={styles.buttonArea}>
            <button
              className={`${styles.btn} ${styles.cancel}`}
              onClick={() => setShowModal(false)}
            >
              キャンセル
            </button>
            <button
              className={`${styles.btn} ${styles.ok}`}
              onClick={() => props.logout()}
            >
              ログアウト
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SideMenuBar;
